/* eslint @typescript-eslint/no-explicit-any: 0 */

import { useMemo } from 'react';
import { Link } from 'react-router';
import type { ProductSubType } from 'types/capi/shared';

import ProductCardInteractionEvent from '~/events/ProductCardInteractionEvent';
import SearchResultClickEvent from '~/events/SearchResultClickEvent';
import useProductCardImpression from '~/hooks/useProductCardImpression';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import { setNavigationContext } from '~/utils/navigationContext';
import { appendTrailingSlash } from '~/utils/trailingSlash';

import { RECORD_SUBTYPES } from './recordTypes';
import SearchResultThumbnail from './SearchResultThumbnail';

type ProductSearchResultProps = {
  hit: any;
  insightsFields: any;
  onClick: (e: any) => object;
  position?: number;
  query: any;
  sub_type: ProductSubType;
  type: string;
};

const MODULE_TYPE_BY_SUB_TYPE: Record<ProductSubType, string> = {
  clothing: 'search-panel-clothing',
  sneaker: 'search-panel-footwear'
};

function getPath({
  canonical_path,
  slug
}: {
  canonical_path: string;
  slug: string;
}) {
  return appendTrailingSlash(
    canonical_path ? canonical_path : `/products/${slug}/`
  );
}

export default function ProductSearchResult({
  onClick,
  position,
  insightsFields = {},
  query,
  hit,
  hit: {
    name,
    objectID,
    price,
    tracking_id,
    variants,
    images: {
      featured: { alt: featuredAlt, original_img_url } = {
        alt: '',
        original_img_url: ''
      }
    } = {}
  },
  sub_type
}: ProductSearchResultProps) {
  const image = original_img_url ? (
    <SearchResultThumbnail
      alt={featuredAlt || name}
      src={original_img_url}
      isSquare={sub_type === RECORD_SUBTYPES.CLOTHING}
    />
  ) : (
    ''
  );

  const link = {
    pathname: getPath(hit as any),
    state: { hit, insightsFields }
  };

  const { isBot } = useRootData();
  const user = useUser();

  // The legacy index keys a product record by `product:<slug>`; the numeric product id every
  // other product_card carries is `tracking_id`, and stock lives on the variants, not the root.
  const [primaryVariant] = variants ?? [];
  const product = useMemo(
    () => ({
      id: tracking_id,
      price,
      stock_status: primaryVariant?.stock_status
    }),
    [price, primaryVariant?.stock_status, tracking_id]
  );
  const moduleType = MODULE_TYPE_BY_SUB_TYPE[sub_type];

  const impressionRef = useProductCardImpression<HTMLAnchorElement>({
    enabled: Boolean(product.stock_status),
    isBot,
    moduleType,
    position: position ?? 1,
    product
  });

  function handleSearchResultClick(e: React.MouseEvent<HTMLElement>) {
    setNavigationContext({
      pagePosition: 'Header' as const,
      position,
      previousPageType: 'search'
    });

    new SearchResultClickEvent({
      isBot,
      product_name: name,
      search_term: query
    }).send();

    new ProductCardInteractionEvent({
      gesture: 'click',
      guestId: user.guestId,
      isBot,
      moduleType,
      position: position ?? 1,
      product,
      searchTerm: query
    }).send();

    onClick(e);
  }

  return (
    <Link
      onClick={(e) => {
        handleSearchResultClick(e);
      }}
      to={link}
      className="mb-3 flex h-16 items-start font-semibold"
      ref={impressionRef}
      rel="product"
    >
      <div className="flex size-16 items-center mix-blend-multiply">
        {image}
      </div>
      <section className="my-auto ml-5 w-4/5">
        <h5
          className="line-clamp-3 font-heading text-sm font-normal"
          id={`aria-product-title--${objectID}`}
        >
          {name}
        </h5>
      </section>
    </Link>
  );
}
